import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { Alert, AlertDescription } from 'designSystem/components/Alert'
import { Box } from 'designSystem/components/Box'
import { Spinner } from 'designSystem/components/Spinner'
import { Text } from 'designSystem/components/Text'
import type { Artifact } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type { DocChatArtifactEvent } from 'features/knowledgeDocumentViewer/types'
import { CircleAlert } from 'lucide-react'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

import { ArtifactPanelHeader } from './ArtifactPanelHeader'
import {
  changedPathsForRevision,
  currentRevisionDownload,
} from './artifactText'
import { artifactRendering } from './kinds'
import { useArtifact } from './useArtifact'

const PanelRoot = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  min-height: 0;
`

const HintLine = styled.div`
  padding: 0 1rem 0.5rem;
  border-bottom: 0.0625rem solid var(--border);
`

export interface ArtifactPanelProps {
  artifactId: string
  siteId: string
  latestEvent?: Pick<DocChatArtifactEvent, 'revision' | 'changed_paths'>
  onOpenInTab?: (artifact: Artifact) => void
  onClose?: () => void
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return t`Something went wrong.`
}

/** Loads an artifact and shows it through its kind's renderer, with a Download of the kind's file. */
export function ArtifactPanel({
  artifactId,
  latestEvent,
  onClose,
  onOpenInTab,
  siteId,
}: ArtifactPanelProps): React.ReactElement {
  useLingui()
  const { artifact, download, error, isDownloading, isLoading } = useArtifact(
    artifactId,
    siteId
  )
  const [actionError, setActionError] = useState<string>()

  const handleDownload = useCallback(() => {
    setActionError(undefined)
    void download().catch((caught: unknown) => {
      setActionError(errorMessage(caught))
    })
  }, [download])

  const handleOpenInTab = useCallback(() => {
    if (artifact && onOpenInTab) onOpenInTab(artifact)
  }, [artifact, onOpenInTab])

  if (isLoading) {
    return (
      <Box display='flex' align='center' justify='center' padding='xl'>
        <Spinner />
      </Box>
    )
  }

  if (error || !artifact) {
    return (
      <Box padding='lg'>
        <Alert variant='destructive' size='sm'>
          <CircleAlert />
          <AlertDescription>
            {error
              ? errorMessage(error)
              : t`This document could not be loaded.`}
          </AlertDescription>
        </Alert>
      </Box>
    )
  }

  const downloaded = currentRevisionDownload(artifact)
  const changedPaths = changedPathsForRevision(latestEvent, artifact.revision)
  const { body } = artifactRendering(artifact)

  return (
    <PanelRoot data-testid='artifact-panel'>
      <ArtifactPanelHeader
        title={artifact.title}
        revision={artifact.revision}
        downloadedAt={downloaded?.at}
        isDownloading={isDownloading}
        onDownload={handleDownload}
        onOpenInTab={onOpenInTab ? handleOpenInTab : undefined}
        onClose={onClose}
      />
      <HintLine>
        <Text variant='caption' color='muted'>
          <Trans>To change this document, tell Cantilever what to edit.</Trans>
        </Text>
      </HintLine>
      {actionError ? (
        <Box paddingX='lg' paddingTop='md'>
          <Alert variant='destructive' size='sm' role='alert'>
            <CircleAlert />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        </Box>
      ) : null}
      {body(changedPaths)}
    </PanelRoot>
  )
}
